
import Vue from 'vue';
import EmptyState from '@/components/states/EmptyState.vue';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import _slice from 'lodash/slice';
import _isNil from 'lodash/isNil';
import { mapState } from 'vuex';
import { db } from '@/firebase';
import { CompanyBalanceRecord } from '@run-diffusion/shared';
import RunnitTokens from '@/views/Runnits/RunnitTokens.vue';

export default Vue.extend({
	name: 'CompanyBalanceRecordsHistory',
	props: {
		value: { type: Number, required: true },
		parentDialogOpen: { type: Boolean, default: false },
        refreshIncrementer: { type: Number, default: 0 },
	},
	data () {
		return {
			currPage: 0,
			pageSize: 50,
			currRecords: [],
			records: [],
			lastRecordDoc: null,
			hasMoreRecords: false,
			loadingInitialRecords: false,
			loadingMoreRecords: false,
			runningTotalsMap: {} as Record<string, number>,
		};
	},
	computed: {
		...mapState([
			'team',
		]),
        currCompanyRunnitTokens () {
            return this.team?.company?.runnitTokens || 0;
        },
		canGoPrevPage () {
			return this.currPage > 0;
		},
		canGoNextPage () {
			return (
				(this.lastRecordDoc && this.hasMoreRecords) ||
				this.currPage < Math.floor(this.records.length / this.pageSize)
			);
		},
	},
	watch: {
        refreshIncrementer: {
            immediate: false,
            handler (newVal: number, oldVal: number) {
                if (newVal > 0 && newVal !== oldVal) {
					this.resetValues();
                    this.loadInitialRecords();
                }
            },
        },
		parentDialogOpen: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (!newVal) {
					this.resetValues();
				}
				if (newVal && newVal !== oldVal) {
					this.loadInitialRecords();
				}
			},
		},
		records: {
            immediate: true,
			handler (newVal: CompanyBalanceRecord[], oldVal: CompanyBalanceRecord[]) {
                if (newVal !== oldVal) {
                    this.runningTotalsMap = {};

                    // Start with current balance and work backwards
                    let runningTotal: number = this.currCompanyRunnitTokens;

                    // Since records are ordered by newest first, we need to add the amounts
                    // backwards to get the historical running total
                    for (let i = 0; i < newVal.length; i++) {
                        const companyBalanceRecord: CompanyBalanceRecord = newVal[i];
                        this.runningTotalsMap[companyBalanceRecord.id] = runningTotal;
                        runningTotal -= companyBalanceRecord.runnitTokensAmount || 0;
                    }
                }
			},
		},
	},
	methods: {
        _isNil,
        resetValues () {
			this.currPage = 0;
			this.currRecords = [];
			this.records = [];
			this.lastRecordDoc = null;
			this.hasMoreRecords = false;
			this.loadingInitialRecords = false;
			this.loadingMoreRecords = false;
			this.runningTotalsMap = {};
        },
		sliceCurrRecords () {
			const startIndex: number = (this.currPage * this.pageSize);
			const endIndex: number = ((this.currPage + 1) * this.pageSize);
			this.currRecords = _slice(
				this.records,
				startIndex,
				endIndex,
			);
		},
		onPrevPage () {
			this.currPage = this.currPage - 1;
			if (this.currPage < 0) this.currPage = 0;

			this.sliceCurrRecords();
		},
		async onNextPage () {
			this.currPage = this.currPage + 1;

			if (this.lastRecordDoc && this.hasMoreRecords) {
				const endIndex: number = (this.currPage * this.pageSize);
				if (endIndex >= this.records.length) {
					await this.loadMoreRecords();
				} else {
					this.sliceCurrRecords();
				}
			} else {
				this.sliceCurrRecords();
			}
		},
		async loadRecords () {
			try {
				let recordsRef: any = db
					.collection(`companies/${this.team.companyId}/companyBalanceRecords`)
					.orderBy('createdAt', 'desc')
					.limit(this.pageSize);

				if (this.lastRecordDoc) {
					recordsRef = recordsRef
						.startAfter(this.lastRecordDoc);
				}

				const recordsSnapshot = await recordsRef.get();
				const result: any[] = recordsSnapshot.docs.map((doc: any) => {
					return {
						...doc.data(),
						get id () { return doc.id },
					};
				});
				const lastDoc: any = recordsSnapshot.empty ? null : recordsSnapshot.docs[recordsSnapshot.docs.length - 1];

				return {
					success: true,
					result,
					lastDoc,
				};
			} catch (e) {
				console.error('Error loading balance records: ', e);
				return {
					success: false,
					result: [],
					lastDoc: null,
				};
			}
		},
		async loadInitialRecords () {
            if (!this.team?.companyId) {
                this.resetValues();
                return;
            }

			if (!this.loadingInitialRecords && !this.loadingMoreRecords) {
				this.loadingInitialRecords = true;
				this.lastRecordDoc = null;
				this.hasMoreRecords = false;
				const { success, result, lastDoc } = await this.loadRecords();
				this.loadingInitialRecords = false;
				if (success) {
					this.currPage = 0;
					this.lastRecordDoc = lastDoc;
					this.hasMoreRecords = result.length === this.pageSize && !!lastDoc;
					this.currRecords = result;
					this.records = this.currRecords;
				}
			}
		},
		async loadMoreRecords () {
			if (!this.loadingInitialRecords && !this.loadingMoreRecords) {
				this.loadingMoreRecords = true;
				const { success, result, lastDoc } = await this.loadRecords();
				this.loadingMoreRecords = false;
				if (success) {
					this.lastRecordDoc = lastDoc;
					this.hasMoreRecords = result.length === this.pageSize && !!lastDoc;
					this.currRecords = result;
					this.records = this.records.concat(this.currRecords);
				}
			}
		},
	},
	components: {
		RunnitTokens,
		LoadingSVG,
		EmptyState,
	},
});
